/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE APP'S OWN ROSTER VOCABULARY, PORTED — his word, 2026-09-19: the phone browser must read
 * *"exactly as the app"*.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * 🔴 **THIS IS A HAND PORT OF SWIFT, AND SAYING SO IS THE POINT.** Both functions below are
 * transcribed from the iOS coach roster, which is the SOURCE:
 *   `HybridTrainer/Services/CoachRosterDates.swift` → `lastTrainedLine(_:now:)`
 *   `HybridTrainer/Views/Coach/ClientsView.swift`   → `countLine`
 * ⚠️ **The repo's standing rule is to GENERATE one side from the other** (`tokens.css`,
 * `lib/equipment.ts`, `lib/goalRequirements.ts` all are), and a hand port is the shape that rule
 * exists to refuse. It is done by hand here because these are FUNCTIONS with branches, not a list
 * of names, and the emitters this repo has all emit lists. **The divergence risk is therefore
 * real and is managed the only other way available: every branch boundary below is pinned by its
 * own clause in `appRoster.test.ts`, with the Swift's own numbers quoted beside it, so a change on
 * one side that is not made here fails a reader rather than nothing.** Filed for a generator.
 *
 * ⚠️ **WHY NOT REUSE `describeLastTraining`:** it is the CLIENT PAGE's sentence and stops at
 * "Trained N days ago", where the app's buckets continue through weeks and months — so a client
 * quiet for nine weeks reads "Trained 63 days ago" in one place and "Trained 9 weeks ago" in the
 * other. `describeLastTraining` now delegates to this, so there is ONE bucket table in the web
 * too and the two surfaces cannot drift apart.
 */

/**
 * The line under an active client's name, in the app's words.
 *
 * ⚠️ **BUCKETS, NOT A FORMATTER** — the Swift's own reason, kept verbatim because it is the whole
 * design: `RelativeDateTimeFormatter` says "1 week ago" for anything from 4 to 11 days, and a
 * coach scanning fifty rows needs TODAY, YESTERDAY and roughly-how-long, in that order of
 * precision and nothing finer.
 *
 * ⚠️ **NEVER-TRAINED IS ITS OWN SENTENCE, NOT A BLANK**, and it is *"No sessions recently"* and
 * never *"never"*: both platforms window the query (the web's roster reads 90 days), so an absent
 * date means "nothing inside the window" and a client who trained hard a year ago would be
 * libelled by "never".
 *
 * ⚠️ **A NEGATIVE AGE IS "Trained today"** — the Swift's `case ..<0`, which exists because a clock
 * skew between a phone and the server can put a finished session a few seconds in the future, and
 * "Trained in 2 days" is how a coach learns to distrust the column.
 */
export function lastTrainedLine(days: number | null): string {
  if (days === null) return "No sessions recently";
  if (days <= 0) return "Trained today";                       // Swift: `..<0` and `0`
  if (days === 1) return "Trained yesterday";
  if (days <= 6) return `Trained ${String(days)} days ago`;     // Swift: `2...6`
  if (days <= 13) return "Trained last week";                   // Swift: `7...13`
  if (days <= 59) {
    // Swift: `14...59` → `days / 7`, INTEGER division.
    return `Trained ${String(Math.floor(days / 7))} weeks ago`;
  }
  // Swift: `max(2, days / 30)` — the floor of 2 stops "1 months ago" at exactly 60 days, where
  // integer division would otherwise say 2 anyway but says 1 for any lower bound a future edit
  // might introduce. Kept because it is the Swift's, not because this branch can reach it.
  return `Trained ${String(Math.max(2, Math.floor(days / 30)))} months ago`;
}

/**
 * The count line under the app's search field: `10 clients · 2 invited`.
 *
 * ⚠️ **THE SECOND HALF IS OMITTED WHEN IT IS ZERO, WHICH IS THE APP'S OWN RULE** (`if p > 0`) —
 * a permanent `· 0 invited` is a figure that is only ever news by being absent, and the app
 * already decided that.
 *
 * ⚠️ **"clients" COUNTS THE ACTIVE ONES ONLY.** The invited are counted separately and by
 * definition are not clients yet; a total that folded them in would tell a coach they have ten
 * people when two of them have never opened anything.
 */
export function rosterCountLine(active: number, invited: number): string {
  const parts = [`${String(active)} ${active === 1 ? "client" : "clients"}`];
  if (invited > 0) parts.push(`${String(invited)} invited`);
  return parts.join(" · ");
}

/**
 * The line under the client's name on the app's client screen.
 *
 * PORTED FROM `HybridTrainer/Views/Coach/ClientDetailView.swift` → `weekSummary`, whose own
 * comment states the design: *"One sentence instead of Everfit's three tiles, counting the week
 * ON SCREEN rather than a rolling window — the week on screen is what the coach is looking at."*
 *
 * ⚠️ **THE BRIEF PARAPHRASED THIS AS `4 of 6 days trained` AND THE APP SAYS `4 of 6 done`.** The
 * app's own string is what ships, because the copy's judge is the app's screen and not a
 * description of it — and because `days` would be wrong twice over: the count is ASSIGNMENTS, of
 * which a day can hold several, and `trained` is a different word from `done` everywhere else on
 * both products.
 *
 * ⚠️ **MISSED IS APPENDED ONLY WHEN IT IS NON-ZERO** — the app's `if missed > 0`. A permanent
 * `· 0 missed` is a figure that is only ever news by being absent.
 *
 * ⚠️ **AND AN EMPTY WEEK IS A SENTENCE, NOT `0 of 0 done`.** Nothing assigned and nothing done
 * are different facts, and the second phrasing reads as a client who ignored their week.
 */
export function weekSummaryLine(week: readonly { done: boolean; missed: boolean }[]): string {
  if (week.length === 0) return "Nothing assigned this week";
  const done = week.filter((a) => a.done).length;
  const missed = week.filter((a) => a.missed).length;
  const line = `${String(done)} of ${String(week.length)} done`;
  return missed > 0 ? `${line} · ${String(missed)} missed` : line;
}
